import Box from "@/components/Box";
import { CMP_PERMISSION_ADMIN } from "@/components/organisations/constants";
import { BASKET_PAYEE_PATIENT } from "@/core/constants";
import ChevronComponent from "@/tpo/Chevron";
import Group from "@/tpo/Group";
import Stack from "@/tpo/Stack";
import isProxyEmail from "@/utils/isProxyEmail";
import isUnderEighteen from "@/utils/isUnderEighteen";
import ButtonV2 from "@/v2/Buttons";

export function getMissingFieldsForPatient(organisationPatient) {
  let editableFields = [];

  if (organisationPatient.patientProfile.user.firstName === "") {
    editableFields.push("firstName");
  }
  if (organisationPatient.patientProfile.user.lastName === "") {
    editableFields.push("lastName");
  }
  if (organisationPatient.patientProfile.dateOfBirth === null) {
    editableFields.push("dateOfBirth");
    editableFields.push("requiresGuardianConsent");
    editableFields.push("guardianFirstName");
    editableFields.push("guardianLastName");
    editableFields.push("guardianEmail");
  }
  if (organisationPatient.patientProfile.guardian) {
    if (isProxyEmail(organisationPatient.patientProfile.guardian.user.email)) {
      editableFields.push("guardianEmail");
    }
  } else {
    if (
      organisationPatient.patientProfile.dateOfBirth &&
      isUnderEighteen(organisationPatient.patientProfile.dateOfBirth)
    ) {
      // then we are missing a guardian so must ask for one
      editableFields.push("guardianEmail");
      editableFields.push("guardianFirstName");
      editableFields.push("guardianLastName");
    } else {
      // email field is irrelevant if we have a guardian saved
      // email will be automatically generated whenever email of guardian is updated
      if (isProxyEmail(organisationPatient.patientProfile.user.email)) {
        editableFields.push("email");
      }
    }
  }

  if (organisationPatient.patientProfile.gender === "") {
    editableFields.push("sex");
  }

  return editableFields;
}

export function getSavePatientEditableFields(organisationPatient) {
  if (!organisationPatient.patientProfile.user.isUserActive) {
    return [
      "firstName",
      "lastName",
      "email",
      "dateOfBirth",
      "sex",
      "guardianFirstName",
      "guardianLastName",
      "guardianEmail",
      "requiresGuardianConsent"
    ];
  }
  return getMissingFieldsForPatient(organisationPatient);
}

export function haveAllPatientDetails(organisationPatient) {
  return getMissingFieldsForPatient(organisationPatient).length === 0;
}

export function canSubmitCheckoutWithPatient(organisationPatient, payee) {
  const editableFields = organisationPatient ? getMissingFieldsForPatient(organisationPatient) : [];

  if (payee === BASKET_PAYEE_PATIENT) {
    return editableFields.length === 0;
  }

  return (
    editableFields.length === 0 ||
    (editableFields.length === 1 && ["email", "guardianEmail"].includes(editableFields[0]))
  );
}

export function getOrganisationPatientsForBasketLineItems(basket, organisationPatients) {
  if (!basket?.multiplePatientsEnabled) {
    return [];
  }

  const patientPks = [
    ...new Set(
      (basket.productLineItems || [])
        .map(line => line.patient?.pk)
        .filter(Boolean)
    )
  ];

  return organisationPatients.filter(patient =>
    patientPks.includes(patient.patientProfile.pk)
  );
}

export function canSubmitCheckoutWithBasketPatients({ basket, organisationPatients, payee }) {
  if (basket?.multiplePatientsEnabled) {
    return getOrganisationPatientsForBasketLineItems(basket, organisationPatients).every(patient =>
      canSubmitCheckoutWithPatient(patient, payee)
    );
  }

  if (!basket?.organisationPatient) {
    return false;
  }

  return canSubmitCheckoutWithPatient(basket.organisationPatient, payee);
}

export default function PatientDetails({
  error,
  patient,
  permissions,
  onEdit,
  buttonProps,
  ...props
}) {
  return (
    <Stack gap={20} data-component-name="PatientDetails" {...props}>
      <Box fontFamily="gilroyBold" fontSize={18}>
        Patient details
      </Box>
      <Box>
        <Group gap={5}>
          <Box as="span" fontSize={[14, 14, 16]} lineHeight="150%">
            Name:
          </Box>
          <Box
            as="span"
            fontSize={[14, 14, 16]}
            lineHeight="150%"
            data-component-name="PatientDetails:name"
          >
            {patient.patientProfile.user.firstName} {patient.patientProfile.user.lastName}
          </Box>
        </Group>
        <Group gap={5}>
          <Box as="span" fontSize={[14, 14, 16]} lineHeight="150%">
            Date of birth:
          </Box>
          <Box
            as="span"
            fontSize={[14, 14, 16]}
            lineHeight="150%"
            data-component-name="PatientDetails:dateOfBirth"
          >
            {patient.patientProfile.dateOfBirth}
          </Box>
        </Group>
        <Group gap={5}>
          <Box as="span" fontSize={[14, 14, 16]} lineHeight="150%">
            Sex:
          </Box>
          <Box
            as="span"
            fontSize={[14, 14, 16]}
            lineHeight="150%"
            data-component-name="PatientDetails:sex"
          >
            {patient.patientProfile.gender}
          </Box>
        </Group>
        <Group>
          <Box as="span" fontSize={[14, 14, 16]} lineHeight="150%">
            Email:
          </Box>
          <Box
            as="span"
            fontSize={[14, 14, 16]}
            lineHeight="150%"
            data-component-name="PatientDetails:email"
          >
            {patient.patientProfile.user.correspondenceEmail}
          </Box>
        </Group>
      </Box>
      {onEdit && permissions &&
        permissions.includes(CMP_PERMISSION_ADMIN) &&
        !haveAllPatientDetails(patient) && (
          <ButtonV2
            color="dark"
            onClick={() => {
              onEdit(true);
            }}
            rightIcon={<ChevronComponent />}
            size={["sm"]}
            mr="auto"
            type="button"
            data-component-name="PatientDetails:edit"
            {...buttonProps}
          >
            Edit
          </ButtonV2>
        )}
      {error}
    </Stack>
  );
}
